import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';
import { ANOMALIES, LAYOUT, REPAIR_TOOLS } from '../utils/constants.js';
import { SurfacePlan } from './SurfacePlan.js';

/* ── Anomaly type definitions ────────────────────────────────── */

const ANOMALY_TYPES = ['alien_ruin', 'crashed_ship', 'monolith', 'abandoned_outpost', 'artifact'];

const ANOMALY_PALETTES = {
  alien_ruin: {
    primary:   [0.60, 0.15, 0.35],
    secondary: [0.58, 0.10, 0.50],
    glow:      [0.55, 0.40, 0.30],
    glowIntensity: 0.12,
  },
  crashed_ship: {
    primary:   [0.00, 0.05, 0.30],
    secondary: [0.08, 0.15, 0.22],
    glow:      [0.10, 0.70, 0.40],
    glowIntensity: 0.08,
  },
  monolith: {
    primary:   [0.70, 0.08, 0.10],
    secondary: [0.72, 0.20, 0.15],
    glow:      [0.72, 0.60, 0.35],
    glowIntensity: 0.25,
  },
  abandoned_outpost: {
    primary:   [0.05, 0.10, 0.35],
    secondary: [0.12, 0.15, 0.25],
    glow:      [0.15, 0.60, 0.35],
    glowIntensity: 0.06,
  },
  artifact: {
    primary:   [0.85, 0.50, 0.50],
    secondary: [0.80, 0.60, 0.40],
    glow:      [0.83, 0.70, 0.50],
    glowIntensity: 0.35,
  },
};

/**
 * Generates rare Points of Interest (POIs) on planet surfaces:
 * alien ruins, crashed ships, monoliths, abandoned outposts, and artifacts.
 *
 * Anomalies are deterministically placed (seeded) and produce serialisable
 * data records for future multiplayer state sync.
 *
 * Each anomaly has an `interacted` flag that can be set by game logic
 * (e.g. player scans/collects the anomaly).
 */
export class AnomalyGenerator {
  constructor() {
    /** @type {{ id: string, type: string, position: {x,y,z}, direction: {x,y,z}, seed: number, interacted: boolean, loot: string|null }[]} */
    this.anomalies = [];
    this.group = new THREE.Group();
    this.group.name = 'anomalies';
    /** @type {{ mesh: THREE.Mesh, data: object, phase: number, baseOpacity: number }[]} */
    this._beacons = [];
  }

  /* ── public API ────────────────────────────────── */

  /**
   * @param {object}      planetData   – planet config
   * @param {number}      planetRadius – SURFACE_RADIUS × size
   * @param {function}    getHeightFn  – (normalizedDir) => surfaceHeight
   * @param {SurfacePlan} [plan]       – shared surface zoning plan
   * @returns {{ group: THREE.Group, anomalies: object[] }}
   */
  generate(planetData, planetRadius, getHeightFn, plan = null) {
    this.clear();

    if (planetData.type === 'gas') {
      return { group: this.group, anomalies: this.anomalies };
    }

    plan = plan || new SurfacePlan(planetData, planetRadius);
    const rng = new SeededRandom(planetData.seed + 77777);

    // Anomalies live at the plan's landmark anchors — evenly spread over the
    // planet, each in the middle of a reserved barren clearing so it reads
    // as a destination instead of drowning in scatter.
    let placed = 0;
    for (const dir of plan.landmarkDirs) {
      const surfaceHeight = getHeightFn(dir);

      // Guarantee the first N anomalies cover all required ruin types for REPAIR_TOOLS.
      // After that, pick randomly from the full set.
      const requiredTypes = REPAIR_TOOLS.map(t => t.ruinType);
      const anomalyType = placed < requiredTypes.length
        ? requiredTypes[placed]
        : rng.pick(ANOMALY_TYPES);
      const anomalySeed = planetData.seed + placed * 313 + 88;

      // Potential loot
      const lootPool = ANOMALIES.LOOT[anomalyType] || [];
      const loot = lootPool.length > 0 ? rng.pick(lootPool) : null;

      const data = {
        id: `anomaly_${planetData.seed}_${placed}`,
        type: anomalyType,
        position: { x: dir.x * surfaceHeight, y: dir.y * surfaceHeight, z: dir.z * surfaceHeight },
        direction: { x: dir.x, y: dir.y, z: dir.z },
        seed: anomalySeed,
        interacted: false,
        loot,
      };
      this.anomalies.push(data);

      const mesh = this._createAnomalyMesh(data);
      this._addBeacon(mesh, data, rng);
      this.group.add(mesh);
      placed++;
    }

    return { group: this.group, anomalies: this.anomalies };
  }

  /**
   * A faint column of light rising from an unexplored anomaly — visible from
   * across the planet so the player always has somewhere to head, but not
   * what awaits them. Hidden once the anomaly has been searched.
   */
  _addBeacon(anomalyGroup, data, rng) {
    const palette = ANOMALY_PALETTES[data.type] || ANOMALY_PALETTES.monolith;
    const color = new THREE.Color().setHSL(
      palette.glow[0], palette.glow[1], Math.min(0.75, palette.glow[2] + 0.2),
    );

    const geo = new THREE.CylinderGeometry(
      LAYOUT.BEACON_RADIUS * 0.4, LAYOUT.BEACON_RADIUS, LAYOUT.BEACON_HEIGHT,
      8, 1, true,
    );
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.18,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const beam = new THREE.Mesh(geo, mat);
    beam.position.y = LAYOUT.BEACON_HEIGHT / 2;
    beam.name = 'anomaly-beacon';
    beam.raycast = () => {}; // never block interaction rays
    anomalyGroup.add(beam);

    this._beacons.push({ mesh: beam, data, phase: rng.next() * 10, baseOpacity: 0.18 });
  }

  clear() {
    this.group.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
        else child.material.dispose();
      }
    });
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    this.anomalies = [];
    this._beacons = [];
  }

  /* ── mesh builders ─────────────────────────────── */

  _createAnomalyMesh(data) {
    const group = new THREE.Group();
    const rng = new SeededRandom(data.seed);
    const dir = new THREE.Vector3(data.direction.x, data.direction.y, data.direction.z);
    const pos = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
    const palette = ANOMALY_PALETTES[data.type] || ANOMALY_PALETTES.monolith;

    const primaryMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...palette.primary),
      roughness: 0.65,
      metalness: 0.15,
    });
    const secondaryMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(...palette.secondary),
      roughness: 0.5,
      metalness: 0.3,
      emissive: new THREE.Color().setHSL(...palette.glow),
      emissiveIntensity: palette.glowIntensity,
    });

    switch (data.type) {
      case 'alien_ruin':       this._buildRuin(group, rng, primaryMat, secondaryMat); break;
      case 'crashed_ship':     this._buildCrashedShip(group, rng, primaryMat, secondaryMat); break;
      case 'monolith':         this._buildMonolith(group, rng, primaryMat, secondaryMat); break;
      case 'abandoned_outpost': this._buildOutpost(group, rng, primaryMat, secondaryMat); break;
      case 'artifact':         this._buildArtifact(group, rng, primaryMat, secondaryMat); break;
    }

    // Position & orient
    group.position.copy(pos);
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    group.quaternion.copy(quat);
    group.rotateOnAxis(new THREE.Vector3(0, 1, 0), rng.next() * Math.PI * 2);

    group.name = `anomaly-${data.id}`;
    group.userData.anomalyData = data;
    return group;
  }

  _buildRuin(group, rng, primaryMat, secondaryMat) {
    // Broken stone pillars in a rough circle
    const pillars = rng.int(5, 10);
    const radius = rng.range(6, 12);
    for (let i = 0; i < pillars; i++) {
      const angle = (i / pillars) * Math.PI * 2 + rng.range(-0.2, 0.2);
      const h = rng.range(3, 8);
      const r = rng.range(0.5, 1.0);
      const pillar = new THREE.Mesh(
        new THREE.CylinderGeometry(r * 0.7, r, h, 5),
        primaryMat,
      );
      pillar.position.set(Math.cos(angle) * radius, h / 2, Math.sin(angle) * radius);
      pillar.rotation.set(rng.range(-0.1, 0.1), 0, rng.range(-0.1, 0.1));
      pillar.castShadow = true;
      group.add(pillar);
    }

    // Central altar/platform
    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(radius * 0.5, radius * 0.6, 0.6, 8),
      secondaryMat,
    );
    platform.position.y = 0.3;
    platform.castShadow = true;
    group.add(platform);

    // Fallen stone blocks scattered around
    const blocks = rng.int(4, 8);
    for (let i = 0; i < blocks; i++) {
      const bw = rng.range(0.6, 2.0);
      const bh = rng.range(0.4, 1.2);
      const bd = rng.range(0.6, 1.6);
      const block = new THREE.Mesh(
        new THREE.BoxGeometry(bw, bh, bd),
        primaryMat,
      );
      const angle = rng.next() * Math.PI * 2;
      const dist = rng.range(2, radius * 1.5);
      block.position.set(Math.cos(angle) * dist, bh / 2, Math.sin(angle) * dist);
      block.rotation.set(rng.range(-0.3, 0.3), rng.next() * Math.PI, rng.range(-0.3, 0.3));
      group.add(block);
    }
  }

  _buildCrashedShip(group, rng, primaryMat, secondaryMat) {
    // Hull — elongated squashed sphere
    const hullLen = rng.range(8, 16);
    const hullR = rng.range(1.8, 3.5);
    const hullGeo = new THREE.CapsuleGeometry(hullR, hullLen, 6, 10);
    hullGeo.rotateZ(Math.PI / 2);
    const hull = new THREE.Mesh(hullGeo, primaryMat);
    hull.position.y = hullR * 0.6;
    hull.rotation.z = rng.range(-0.3, 0.3);
    hull.castShadow = true;
    group.add(hull);

    // Broken wing
    const wing = new THREE.Mesh(
      new THREE.BoxGeometry(hullLen * 0.6, 0.15, hullR * 3.0),
      primaryMat,
    );
    wing.position.set(-hullLen * 0.15, hullR * 0.5, hullR * 0.8);
    wing.rotation.x = rng.range(0.1, 0.4);
    group.add(wing);

    // Sparking/glowing damage area
    const damage = new THREE.Mesh(
      new THREE.SphereGeometry(hullR * 0.5, 6, 6),
      secondaryMat,
    );
    damage.position.set(hullLen * 0.3, hullR * 0.5, 0);
    group.add(damage);

    // Debris scatter
    const debris = rng.int(6, 12);
    for (let i = 0; i < debris; i++) {
      const size = rng.range(0.3, 1.0);
      const piece = new THREE.Mesh(
        new THREE.BoxGeometry(size, size * 0.5, size * 0.7),
        primaryMat,
      );
      const angle = rng.next() * Math.PI * 2;
      const dist = rng.range(hullLen * 0.3, hullLen * 1.2);
      piece.position.set(Math.cos(angle) * dist, size * 0.25, Math.sin(angle) * dist);
      piece.rotation.set(rng.next(), rng.next(), rng.next());
      group.add(piece);
    }
  }

  _buildMonolith(group, rng, primaryMat, secondaryMat) {
    // Tall rectangular slab
    const h = rng.range(8, 16);
    const w = rng.range(2.5, 5);
    const d = rng.range(0.5, 1.2);
    const slab = new THREE.Mesh(
      new THREE.BoxGeometry(w, h, d),
      primaryMat,
    );
    slab.position.y = h / 2;
    slab.castShadow = true;
    group.add(slab);

    // Glowing inscription lines
    const lines = rng.int(3, 6);
    for (let i = 0; i < lines; i++) {
      const lineH = 0.06;
      const lineW = w * rng.range(0.4, 0.8);
      const line = new THREE.Mesh(
        new THREE.BoxGeometry(lineW, lineH, d + 0.02),
        secondaryMat,
      );
      line.position.y = h * 0.2 + i * (h * 0.1);
      group.add(line);
    }

    // Base stones
    const baseStones = rng.int(2, 4);
    for (let i = 0; i < baseStones; i++) {
      const sr = rng.range(0.4, 0.8);
      const stone = new THREE.Mesh(
        new THREE.DodecahedronGeometry(sr, 0),
        primaryMat,
      );
      const angle = rng.next() * Math.PI * 2;
      stone.position.set(Math.cos(angle) * w, sr * 0.5, Math.sin(angle) * w);
      group.add(stone);
    }
  }

  _buildOutpost(group, rng, primaryMat, secondaryMat) {
    // Main structure — box/dome
    const size = rng.range(3.5, 6.5);
    const main = new THREE.Mesh(
      new THREE.BoxGeometry(size, size * 0.7, size),
      primaryMat,
    );
    main.position.y = size * 0.35;
    main.castShadow = true;
    group.add(main);

    // Dome on top
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(size * 0.5, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.5),
      secondaryMat,
    );
    dome.position.y = size * 0.7;
    group.add(dome);

    // Solar panel / antenna
    const antennaH = rng.range(3, 6);
    const antenna = new THREE.Mesh(
      new THREE.CylinderGeometry(0.1, 0.15, antennaH, 4),
      primaryMat,
    );
    antenna.position.set(size * 0.4, size * 0.7 + antennaH / 2, 0);
    group.add(antenna);

    // Dish at top
    const dish = new THREE.Mesh(
      new THREE.SphereGeometry(0.8, 6, 4, 0, Math.PI * 2, 0, Math.PI * 0.5),
      secondaryMat,
    );
    dish.position.set(size * 0.4, size * 0.7 + antennaH, 0);
    dish.rotation.x = -0.5;
    group.add(dish);

    // Supply crates nearby
    const crates = rng.int(3, 6);
    for (let i = 0; i < crates; i++) {
      const cs = rng.range(0.6, 1.2);
      const crate = new THREE.Mesh(
        new THREE.BoxGeometry(cs, cs, cs),
        primaryMat,
      );
      const angle = rng.next() * Math.PI * 2;
      const dist = rng.range(size * 0.8, size * 2);
      crate.position.set(Math.cos(angle) * dist, cs / 2, Math.sin(angle) * dist);
      crate.rotation.y = rng.next() * Math.PI;
      group.add(crate);
    }
  }

  _buildArtifact(group, rng, primaryMat, secondaryMat) {
    // Floating geometric shape
    const shape = rng.pick(['octahedron', 'dodecahedron', 'icosahedron']);
    const r = rng.range(1.0, 2.0);
    let geo;
    switch (shape) {
      case 'octahedron':    geo = new THREE.OctahedronGeometry(r, 0); break;
      case 'dodecahedron':  geo = new THREE.DodecahedronGeometry(r, 0); break;
      default:              geo = new THREE.IcosahedronGeometry(r, 0); break;
    }
    const artifact = new THREE.Mesh(geo, secondaryMat);
    artifact.position.y = r + 2.5;
    artifact.castShadow = true;
    artifact.name = 'artifact-core';
    group.add(artifact);

    // Pedestal
    const pedestal = new THREE.Mesh(
      new THREE.CylinderGeometry(r * 0.8, r * 1.0, 1.5, 6),
      primaryMat,
    );
    pedestal.position.y = 0.75;
    group.add(pedestal);

    // Orbiting rings
    const ringGeo = new THREE.TorusGeometry(r * 1.8, 0.05, 6, 32);
    const ring = new THREE.Mesh(ringGeo, secondaryMat);
    ring.position.y = r + 2.5;
    ring.rotation.x = rng.range(0.3, 0.8);
    group.add(ring);
  }

  /**
   * Animate anomalies (floating artifacts, pulsing glows).
   * @param {number} dt    – delta time
   * @param {number} time  – elapsed time
   */
  update(dt, time) {
    this.group.traverse(child => {
      if (child.name === 'artifact-core') {
        // Slow rotation + bob
        child.rotation.y += dt * 0.5;
        child.rotation.x += dt * 0.2;
        child.position.y += Math.sin(time * 1.5) * 0.002;
      }
    });

    // Beacons breathe slowly while unexplored, vanish once searched.
    for (const b of this._beacons) {
      b.mesh.visible = !b.data.interacted;
      if (!b.mesh.visible) continue;
      b.mesh.material.opacity = b.baseOpacity * (0.7 + 0.3 * Math.sin(time * 1.6 + b.phase));
    }
  }
}
